import CustomStatusLabel from 'components/commons/CustomStatusLabel';
import { IRestColumnProps } from 'components/rest/Table/TableColumn';
import { ORDER_TYPES, SELLER_ORDER_STATUS_CONST } from 'constants/constants';
import FilterLocation from 'containers/Inventory/components/FilterLocation';
import RestList from 'containers/rest/List';
import { useGetFilter } from 'hooks/useSearchParamsObj';
import { upperFirst } from 'lodash';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { DynamicObjectType, FilterFromUrlResponseType } from 'types/commons';
import { formatDateTime } from 'utils/formattedString';

const breadcrumbs = [
  {
    title: 'In Process Orders',
    href: '/inventory/inProcessOrder',
  },
];

const List = () => {
  const { t } = useTranslation();
  const { searchParams } = useGetFilter();
  const locationId = searchParams.get('locationId');

  const restInitialFilter: FilterFromUrlResponseType = useMemo(() => {
    const orderStatus = `order.statusOms||$in||${[
      SELLER_ORDER_STATUS_CONST.CONFIRMED.value,
      SELLER_ORDER_STATUS_CONST.READY_TO_PICK.value,
      SELLER_ORDER_STATUS_CONST.PICKING.value,
      SELLER_ORDER_STATUS_CONST.PACKING.value,
      'READY_TO_TRY',
    ].join(',')}`;

    return {
      join: ['order'],
      filter: locationId
        ? [`locationId||$eq||${locationId}`, orderStatus]
        : orderStatus,
    };
  }, [locationId]);

  const columns: IRestColumnProps[] = [
    {
      name: 'variantSku',
      title: t('inProcessOrders.variantSku'),
      isDefault: true,
    },
    {
      name: 'locationId',
      title: t('orders.warehouse'),
      isDefault: true,
    },
    {
      name: 'quantity',
      title: t('orders.quantity'),
      isDefault: true,
    },
    {
      name: 'order.orderNumber',
      title: t('orders.orderNumber'),
      isDefault: true,
      render: (dt, rc: DynamicObjectType) => (
        <Link
          className="text-highlight"
          to={`/order-management/${rc?.orderId}/show`}
        >{`${dt}`}</Link>
      ),
    },
    {
      name: 'order.saleOrderId',
      title: t('orders.saleOrderId'),
      isDefault: true,
    },
    {
      name: 'order.createdAt',
      title: t('orders.createdAt'),
      isDefault: true,
      render: (d) => (
        <p style={{ minWidth: 150 }}>{d ? formatDateTime(d) : ''}</p>
      ),
    },
    {
      name: 'order.statusOms',
      title: t('orders.status'),
      isDefault: true,
      render: (d: string) => (
        <CustomStatusLabel
          dataSource={{
            ...SELLER_ORDER_STATUS_CONST,
            READY_TO_TRY: {
              value: 'READY_TO_TRY',
              text: 'orderTrackingStatus.readyToTry',
              isDefault: true,
              isRefund: true,
              index: 15,
              isPickup: true,
              color: '#6852D3',
              background: '#6852D31A',
            },
          }}
          record={{ status: d }}
          statusKey="status"
        />
      ),
    },
    {
      name: 'order.channel',
      title: t('orders.channel'),
      render: (d: string) => <>{upperFirst(d)}</>,
      isDefault: true,
    },
    {
      name: 'order.shippingType',
      title: t('orders.type'),
      isDefault: true,
      render: (d) => (
        <>{t(ORDER_TYPES.find((item) => item?.value === d)?.text)}</>
      ),
    },
  ];

  const extraActionsFilter = useMemo(
    () => <FilterLocation keyFilter="locationId" valueOptionKey="id" />,
    [],
  );

  return (
    <RestList
      initialFilter={restInitialFilter}
      columns={columns}
      model="inProcessOrders"
      breadcrumbs={breadcrumbs}
      hasSearch
      hasImport={false}
      hasExport={false}
      hasFilter
      hasOnlyFilter
      searchKeys={['variantSku', 'order.orderNumber']}
      placeholderInputSearch="Search order by  Variant SKU, Order No"
      extraActionFilters={extraActionsFilter}
    />
  );
};

export default List;
